/**
 * SSC email signup: the one shared module for every signup spot (footer band, homepage early-access
 * strip, header NEWSLETTER panel, burger-menu NEWSLETTER field, product page country waitlist and
 * back-in-stock "notify me"). Our own markup and design; Klaviyo is only the backend via its client
 * APIs (public company ID only, never a private key; no klaviyo.js needed).
 * https://developers.klaviyo.com/en/reference/create_client_subscription
 * https://developers.klaviyo.com/en/reference/create_client_back_in_stock_subscription
 *
 * Markup contract ([data-ssc-signup] wrapper, see snippets/ssc-signup-form.liquid, blocks/ssc-drop-strip.liquid,
 * blocks/ssc-country-waitlist.liquid and blocks/ssc-notify-me.liquid):
 *   data-ssc-signup                       "" (list subscription, default) or "back-in-stock"
 *   data-company-id                       Klaviyo public company ID
 *   data-list-id                          list ID (list subscriptions)
 *   data-variant-id                       Shopify variant ID (back-in-stock)
 *   data-source                           custom_source, e.g. "Footer"
 *   data-signup-source                    profile property signup_source, e.g. "footer"
 *   data-properties                       optional JSON object of extra profile properties
 *   data-repeat-key                       optional; separate "already signed up" memory for this form
 *   data-error-invalid / -failed          messages; data-success-already for a repeat signup
 *   [data-form] with input[type=email], [data-submit], honeypot input[data-honeypot]
 *   optional fields inside the form marked data-property="name": their value is merged into the
 *     profile properties; a select may also carry data-property-text="name" to send the selected
 *     option's text (e.g. the country name next to its code)
 *   [data-error] (role=alert) and [data-success] (role=status, hidden)
 *
 * One document-level submit listener handles all forms, including ones rendered later (drawer).
 * Buttons are enabled in the markup; forms use method="post", so without JS no address lands in a URL.
 *
 * States: invalid email (inline error), sending (button disabled, aria-busy, .is-loading),
 * success (form hidden, success shown), already submitted in this browser for the same list or
 * variant (same, with the "already" message, no request), failed (generic retry message, button
 * enabled again). Honeypot filled (bots): pretend success, send nothing.
 * SSCKlaviyo.reset(root) brings a form back to its initial state (used when the variant changes).
 */
(function () {
  'use strict';

  // Included once per form; only the first copy installs the listeners
  if (window.SSCKlaviyo) return;

  var KLAVIYO_BASE = 'https://a.klaviyo.com/client/';
  var KLAVIYO_REVISION = '2026-07-15';
  var EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  var STORAGE_KEY = 'ssc-signup-submitted';

  /**
   * POSTs a JSON:API body to a Klaviyo client endpoint.
   * @param {string} path - e.g. 'subscriptions'
   * @param {string} companyId
   * @param {Object} body
   * @returns {Promise<void>} resolves on 202 Accepted, rejects otherwise
   */
  function post(path, companyId, body) {
    return fetch(KLAVIYO_BASE + path + '?company_id=' + encodeURIComponent(companyId), {
      method: 'POST',
      // Klaviyo's JSON:API media type (per the API reference)
      headers: { 'Content-Type': 'application/vnd.api+json', revision: KLAVIYO_REVISION },
      body: JSON.stringify(body)
    }).then(function (response) {
      // Klaviyo answers 202 Accepted; anything else counts as a failure
      if (response.status !== 202) throw new Error('Klaviyo ' + response.status);
    });
  }

  /**
   * @param {{companyId: string, listId: string, email: string, source?: string, properties?: Object}} options
   * @returns {Promise<void>}
   */
  function subscribe(options) {
    var attributes = { email: options.email };
    if (options.properties && Object.keys(options.properties).length) attributes.properties = options.properties;
    var body = {
      data: {
        type: 'subscription',
        attributes: { profile: { data: { type: 'profile', attributes: attributes } } },
        relationships: { list: { data: { type: 'list', id: options.listId } } }
      }
    };
    if (options.source) body.data.attributes.custom_source = options.source;
    return post('subscriptions', options.companyId, body);
  }

  /**
   * Back-in-stock subscription for one Shopify variant (email channel).
   * @param {{companyId: string, variantId: string, email: string}} options
   * @returns {Promise<void>}
   */
  function subscribeBackInStock(options) {
    return post('back-in-stock-subscriptions', options.companyId, {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: { data: { type: 'profile', attributes: { email: options.email } } }
        },
        relationships: {
          variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + options.variantId } }
        }
      }
    });
  }

  /** Small non-reversible fingerprint, so the browser never stores the address itself */
  function fingerprint(value) {
    var hash = 5381;
    for (var i = 0; i < value.length; i++) hash = ((hash << 5) + hash + value.charCodeAt(i)) | 0;
    return String(hash >>> 0);
  }

  function readSubmitted() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    } catch (e) {
      return [];
    }
  }

  /** Repeat detection per address and target (list or back-in-stock variant) */
  function submissionKey(email, target) {
    return fingerprint(email + '|' + (target || ''));
  }

  function rememberSubmitted(key) {
    try {
      var list = readSubmitted();
      if (list.indexOf(key) === -1) list.push(key);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(-20)));
    } catch (e) {
      /* storage unavailable: no repeat detection */
    }
  }

  function wasSubmitted(key) {
    return readSubmitted().indexOf(key) !== -1;
  }

  function parseProperties(root, form) {
    var properties = {};
    if (root.dataset.properties) {
      try {
        properties = JSON.parse(root.dataset.properties) || {};
      } catch (e) {
        properties = {};
      }
    }
    if (root.dataset.signupSource) properties.signup_source = root.dataset.signupSource;

    // Extra fields (e.g. waitlist country and demand); empty values are left out
    var fields = form.querySelectorAll('[data-property]');
    for (var i = 0; i < fields.length; i++) {
      var field = fields[i];
      var value = (field.value || '').trim();
      if (!value) continue;
      properties[field.dataset.property] = value;
      if (field instanceof HTMLSelectElement && field.dataset.propertyText) {
        var option = field.options[field.selectedIndex];
        if (option) properties[field.dataset.propertyText] = option.textContent.trim();
      }
    }
    return properties;
  }

  function setError(root, input, message) {
    var error = root.querySelector('[data-error]');
    if (error) error.textContent = message || '';
    if (message) input.setAttribute('aria-invalid', 'true');
    else input.removeAttribute('aria-invalid');
  }

  function setSending(root, form, button, on) {
    root.__sscSending = on;
    if (button) button.disabled = on;
    root.classList.toggle('is-loading', on);
    if (on) form.setAttribute('aria-busy', 'true');
    else form.removeAttribute('aria-busy');
  }

  function showSuccess(root, form, message) {
    var success = root.querySelector('[data-success]');
    if (success) {
      if (success.__sscDefault === undefined) success.__sscDefault = success.textContent;
      success.textContent = message || success.__sscDefault;
      success.hidden = false;
    }
    form.hidden = true;
    root.classList.add('is-success');
  }

  /** Back to the initial state: form shown, no error, no success */
  function reset(root) {
    if (!root) return;
    var form = root.querySelector('[data-form]');
    var success = root.querySelector('[data-success]');
    var input = form && form.querySelector('input[type="email"]');
    if (success) {
      if (success.__sscDefault !== undefined) success.textContent = success.__sscDefault;
      success.hidden = true;
    }
    if (form) {
      form.hidden = false;
      setSending(root, form, form.querySelector('[data-submit]'), false);
    }
    if (input) setError(root, input, '');
    root.classList.remove('is-success');
  }

  /**
   * One delegated submit handler for every [data-ssc-signup] form, so forms that are rendered or
   * re-rendered later (e.g. the burger-menu drawer) work without re-initialising.
   */
  function onSubmit(event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-form]')) return;
    var root = form.closest('[data-ssc-signup]');
    if (!root) return;
    event.preventDefault();
    if (root.__sscSending) return;

    var input = form.querySelector('input[type="email"]');
    var button = form.querySelector('[data-submit]');
    var honeypot = form.querySelector('[data-honeypot]');
    var success = root.querySelector('[data-success]');
    var isBackInStock = root.dataset.sscSignup === 'back-in-stock';

    // Bots fill every field: pretend it worked, send nothing
    if (honeypot && honeypot.value) {
      showSuccess(root, form);
      return;
    }

    var email = input.value.trim().toLowerCase();
    if (!EMAIL_PATTERN.test(email)) {
      setError(root, input, root.dataset.errorInvalid);
      input.focus();
      return;
    }
    setError(root, input, '');

    var target = isBackInStock ? root.dataset.variantId : root.dataset.listId;
    if (!root.dataset.companyId || !target) {
      setError(root, input, root.dataset.errorFailed);
      return;
    }

    // data-repeat-key separates repeat detection for a form on the same list (e.g. members waitlist vs footer)
    var key = submissionKey(
      email,
      (isBackInStock ? 'bis:' : 'list:') + target + (root.dataset.repeatKey ? ':' + root.dataset.repeatKey : '')
    );
    if (wasSubmitted(key)) {
      showSuccess(root, form, root.dataset.successAlready || (success && success.textContent));
      return;
    }

    setSending(root, form, button, true);
    var request = isBackInStock
      ? subscribeBackInStock({ companyId: root.dataset.companyId, variantId: target, email: email })
      : subscribe({
          companyId: root.dataset.companyId,
          listId: target,
          email: email,
          source: root.dataset.source,
          properties: parseProperties(root, form)
        });

    request
      .then(function () {
        rememberSubmitted(key);
        setSending(root, form, button, false);
        showSuccess(root, form);
      })
      .catch(function () {
        setSending(root, form, button, false);
        setError(root, input, root.dataset.errorFailed);
      });
  }

  function onInput(event) {
    var input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'email') return;
    var root = input.closest('[data-ssc-signup]');
    if (!root) return;
    var error = root.querySelector('[data-error]');
    if (error && error.textContent) setError(root, input, '');
  }

  /** Kept for compatibility (theme editor reloads); delegation needs no per-form setup */
  function init() {}

  document.addEventListener('submit', onSubmit);
  document.addEventListener('input', onInput);

  window.SSCKlaviyo = {
    subscribe: subscribe,
    subscribeBackInStock: subscribeBackInStock,
    reset: reset,
    emailPattern: EMAIL_PATTERN,
    init: init
  };
})();
